import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Heart } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_da037825-faa5-420e-a579-84f9393da584/artifacts/qxg7ev86_redeemlogo.jpeg";

const SOCIALS = [
  {
    name: "Instagram",
    url: "https://www.instagram.com/rccg_dominionparish/",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
    ),
  },
  {
    name: "Facebook",
    url: "https://www.facebook.com/profile.php?id=61588922004687",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
    ),
  },
  {
    name: "TikTok",
    url: "https://www.tiktok.com/@rccg_dominions",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
    ),
  },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-navy text-white relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cgreen via-crimson to-cgreen" />

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="RCCG" className="w-12 h-12 rounded-full" />
              <div>
                <p className="font-heading font-bold text-lg">Dominion Parish</p>
                <p className="text-white/50 text-xs font-body">RCCG Felixstowe</p>
              </div>
            </div>
            <p className="font-body text-white/80 text-sm leading-relaxed mb-5">
              Empowered to reign. Called to transform. Most importantly &mdash; to make heaven.
            </p>
            {/* Social icons */}
            <div className="flex items-center gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-white/20 hover:text-white transition-all"
                  data-testid={`social-${s.name.toLowerCase()}`}
                  title={s.name}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <h4 className="font-heading font-bold text-base mb-4">Quick Links</h4>
            <div className="space-y-2">
              {[
                { label: "Home", target: "home" },
                { label: "About Us", target: "about" },
                { label: "Service Times", target: "services" },
                { label: "Events", target: "events" },
                { label: "Contact", target: "contact" },
                { label: "Prayer Request", target: "prayer" },
              ].map((link) => (
                <button
                  key={link.target}
                  onClick={() => document.getElementById(link.target)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="block font-body text-white/80 text-sm hover:text-white transition-colors cursor-pointer"
                >
                  {link.label}
                </button>
              ))}
              <a href="/control" className="block font-body text-white/60 text-sm hover:text-white transition-colors">
                Control Centre
              </a>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <h4 className="font-heading font-bold text-base mb-4">Contact</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-cgreen mt-0.5 shrink-0" />
                <p className="font-body text-white/80 text-sm">
                  Walton Community Hall, 274 High Street,<br />
                  Walton, Felixstowe, IP11 9DS
                </p>
              </div>
              <a href="tel:07738998165" className="flex items-center gap-3 hover:text-white transition-colors">
                <Phone className="w-4 h-4 text-cgreen shrink-0" />
                <p className="font-body text-white/80 text-sm">07738998165</p>
              </a>
              <a href="mailto:dominionparish0@gmail.com" className="flex items-center gap-3 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-cgreen shrink-0" />
                <p className="font-body text-white/80 text-sm">dominionparish0@gmail.com</p>
              </a>
            </div>
          </motion.div>

          {/* Follow Us */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <h4 className="font-heading font-bold text-base mb-4">Follow Us</h4>
            <div className="space-y-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                >
                  <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">{s.icon}</span>
                  <span className="font-body text-sm">{s.name}</span>
                </a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-body text-white/70 text-xs">
            &copy; {new Date().getFullYear()} RCCG Dominion Parish, Felixstowe. All rights reserved.
          </p>
          <p className="font-body text-white/70 text-xs flex items-center gap-1">
            Made with <Heart className="w-3 h-3 text-crimson" /> for the Kingdom
          </p>
        </div>
      </div>
    </footer>
  );
}
